// Image MIME types
export const MIME_TYPE_JPEG = 'image/jpeg' as const;
export type MimeTypeJpeg = typeof MIME_TYPE_JPEG;
export const MIME_TYPE_JPG = 'image/jpg' as const;
export type MimeTypeJpg = typeof MIME_TYPE_JPG;
export const MIME_TYPE_PNG = 'image/png' as const;
export type MimeTypePng = typeof MIME_TYPE_PNG;
export const MIME_TYPE_GIF = 'image/gif' as const;
export type MimeTypeGif = typeof MIME_TYPE_GIF;
export const MIME_TYPE_HEIC = 'image/heic' as const;
export type MimeTypeHeic = typeof MIME_TYPE_HEIC;
export const MIME_TYPE_HEIF = 'image/heif' as const;
export type MimeTypeHeif = typeof MIME_TYPE_HEIF;
export const MIME_TYPE_SVG = 'image/svg+xml' as const;
export type MimeTypeSvg = typeof MIME_TYPE_SVG;
export const MIME_TYPE_ICO = 'image/x-icon' as const;
export type MimeTypeIco = typeof MIME_TYPE_ICO;
export const MIME_TYPE_WEBP = 'image/webp' as const;
export type MimeTypeWebp = typeof MIME_TYPE_WEBP;

export type ImageMimeType =
    | MimeTypeJpeg
    | MimeTypeJpg
    | MimeTypePng
    | MimeTypeGif
    | MimeTypeHeic
    | MimeTypeHeif
    | MimeTypeSvg
    | MimeTypeIco
    | MimeTypeWebp;

// Video MIME types
export const MIME_TYPE_MP4 = 'video/mp4' as const;
export type MimeTypeMp4 = typeof MIME_TYPE_MP4;
export const MIME_TYPE_WEBM = 'video/webm' as const;
export type MimeTypeWebm = typeof MIME_TYPE_WEBM;
export const MIME_TYPE_OGV = 'video/ogg' as const;
export type MimeTypeOgv = typeof MIME_TYPE_OGV;
export const MIME_TYPE_AVI = 'video/avi' as const;
export type MimeTypeAvi = typeof MIME_TYPE_AVI;
export const MIME_TYPE_MOV = 'video/quicktime' as const;
export type MimeTypeMov = typeof MIME_TYPE_MOV;
export const MIME_TYPE_MKV = 'video/x-matroska' as const;
export type MimeTypeMkv = typeof MIME_TYPE_MKV;
export const MIME_TYPE_FLV = 'video/x-flv' as const;
export type MimeTypeFlv = typeof MIME_TYPE_FLV;
export type VideoMimeType =
    | MimeTypeMp4
    | MimeTypeWebm
    | MimeTypeOgv
    | MimeTypeAvi
    | MimeTypeMov
    | MimeTypeMkv
    | MimeTypeFlv;

// Document MIME types
export const MIME_TYPE_PDF = 'application/pdf' as const;
export type MimeTypePdf = typeof MIME_TYPE_PDF;
export const MIME_TYPE_DOC = 'application/msword' as const;
export type MimeTypeDoc = typeof MIME_TYPE_DOC;
export const MIME_TYPE_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' as const;
export type MimeTypeDocx = typeof MIME_TYPE_DOCX;
export const MIME_TYPE_XLS = 'application/vnd.ms-excel' as const;
export type MimeTypeXls = typeof MIME_TYPE_XLS;
export const MIME_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' as const;
export type MimeTypeXlsx = typeof MIME_TYPE_XLSX;
export const MIME_TYPE_TXT = 'text/plain' as const;
export type MimeTypeTxt = typeof MIME_TYPE_TXT;
export const MIME_TYPE_APPLICATION_RTF = 'application/rtf' as const;
export type MimeTypeApplicationRtf = typeof MIME_TYPE_APPLICATION_RTF;
export const MIME_TYPE_RTF = 'text/rtf' as const;
export type MimeTypeRtf = typeof MIME_TYPE_RTF;
export const MIME_TYPE_XML = 'text/xml' as const;
export type MimeTypeXml = typeof MIME_TYPE_XML;
export const MIME_TYPE_APPLICATION_XML = 'application/xml' as const;
export type MimeTypeApplicationXml = typeof MIME_TYPE_APPLICATION_XML;
export type DocumentMimeType =
    | MimeTypePdf
    | MimeTypeDoc
    | MimeTypeDocx
    | MimeTypeXls
    | MimeTypeXlsx
    | MimeTypeTxt
    | MimeTypeRtf
    | MimeTypeApplicationRtf
    | MimeTypeXml
    | MimeTypeApplicationXml;

// Fonts MIME types
export const MIME_TYPE_APPLICATION_WOFF = 'application/font-woff' as const;
export type MimeTypeApplicationWoff = typeof MIME_TYPE_APPLICATION_WOFF;
export const MIME_TYPE_WOFF = 'font/woff' as const;
export type MimeTypeWoff = typeof MIME_TYPE_WOFF;
export const MIME_TYPE_WOFF2 = 'font/woff2' as const;
export type MimeTypeWoff2 = typeof MIME_TYPE_WOFF2;
export type FontsMimeType = MimeTypeWoff | MimeTypeWoff2 | MimeTypeApplicationWoff;

export const EXTENSION_WOFF = '.woff' as const;
export type ExtensionWoff = typeof EXTENSION_WOFF;
export const EXTENSION_WOFF2 = '.woff2' as const;
export type ExtensionWoff2 = typeof EXTENSION_WOFF2;
export type FontsExtensions = ExtensionWoff | ExtensionWoff2;

// Mime type union
export type AllMimeTypes = ImageMimeType | VideoMimeType | DocumentMimeType | FontsMimeType;

// MIME types mapping
export const IMAGE_MIME_TYPES: Record<ImageMimeType, string> = {
    [MIME_TYPE_JPEG]: 'JPEG',
    [MIME_TYPE_JPG]: 'JPG',
    [MIME_TYPE_PNG]: 'PNG',
    [MIME_TYPE_GIF]: 'GIF',
    [MIME_TYPE_HEIC]: 'HEIC',
    [MIME_TYPE_HEIF]: 'HEIF',
    [MIME_TYPE_SVG]: 'SVG',
    [MIME_TYPE_ICO]: 'ICO',
    [MIME_TYPE_WEBP]: 'WEBP',
};

export const VIDEO_MIME_TYPES: Record<VideoMimeType, string> = {
    [MIME_TYPE_MP4]: 'MP4',
    [MIME_TYPE_WEBM]: 'WEBM',
    [MIME_TYPE_OGV]: 'OGV',
    [MIME_TYPE_AVI]: 'AVI',
    [MIME_TYPE_MOV]: 'MOV',
    [MIME_TYPE_MKV]: 'MKV',
    [MIME_TYPE_FLV]: 'FLV',
};

export const DOCUMENT_MIME_TYPES: Record<DocumentMimeType, string> = {
    [MIME_TYPE_PDF]: 'PDF',
    [MIME_TYPE_DOC]: 'DOC',
    [MIME_TYPE_DOCX]: 'DOCX',
    [MIME_TYPE_TXT]: 'TXT',
    [MIME_TYPE_XLS]: 'XLS',
    [MIME_TYPE_XLSX]: 'XLSX',
    [MIME_TYPE_RTF]: 'RTF',
    [MIME_TYPE_APPLICATION_RTF]: 'RTF',
    [MIME_TYPE_XML]: 'XML',
    [MIME_TYPE_APPLICATION_XML]: 'XML',
};

export const FONTS_MIME_TYPES: Record<FontsMimeType, string> = {
    [MIME_TYPE_WOFF]: 'WOFF',
    [MIME_TYPE_APPLICATION_WOFF]: 'WOFF',
    [MIME_TYPE_WOFF2]: 'WOFF2',
};

export const FONTS_EXTENSIONS: Record<FontsExtensions, string> = {
    [EXTENSION_WOFF]: 'WOFF',
    [EXTENSION_WOFF2]: 'WOFF2',
};

export const MIME_TYPES: Record<AllMimeTypes, string> = {
    ...IMAGE_MIME_TYPES,
    ...VIDEO_MIME_TYPES,
    ...DOCUMENT_MIME_TYPES,
    ...FONTS_MIME_TYPES,
};
